import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import SideNavbar from "../../components/common/SideNavbar";
import TopNavbar from "../../components/common/TopNavbar";
import { userTableCustomStyles } from "../../utils/TableCustomStyles";

const HR_BASE = `${process.env.REACT_APP_BACKEND_API_URL}/hr`;
const USER_BASE = `${process.env.REACT_APP_BACKEND_API_URL}/user`;

function EmployeeList() {
  const navigate = useNavigate();
  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");
  const [rows, setRows] = useState([]);
  const [dataPending, setDataPending] = useState(true);
  const [search, setSearch] = useState("");

  const handleDataFromTopNavBar = (permissiondata) => {
    setDataFromTopNavBar(permissiondata);
  };

  const loadData = async () => {
    try {
      setDataPending(true);
      const [usersRes, employeesRes] = await Promise.all([
        axios.get(`${USER_BASE}/user/`),
        axios.get(`${HR_BASE}/employee/`),
      ]);
      const employeesByUserId = {};
      (employeesRes.data || []).forEach((employee) => {
        const userId = employee.user_data?.id ?? employee.user;
        employeesByUserId[userId] = employee;
      });

      const combined = (usersRes.data || [])
        .filter((user) => user.is_employee === "YES")
        .map((user) => {
          const employee = employeesByUserId[user.id];
          return {
            userId: user.id,
            employeeId: employee?.id ?? null,
            name: user.name || user.email || user.username || "-",
            email: user.email || "-",
            employeeCode: employee?.employee_code || "-",
            department: employee?.department_data?.name || "-",
            designation: employee?.designation_data?.title || "-",
            status: employee ? (employee.status || "ACTIVE") : "SETUP_PENDING",
          };
        });

      setRows(combined);
    } catch (err) {
      toast.error("Error fetching employees.");
    } finally {
      setDataPending(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.name, row.email, row.employeeCode, row.department, row.designation].some((value) =>
        (value || "").toLowerCase().includes(term)
      )
    );
  }, [rows, search]);

  const statusBadge = (status) => {
    if (status === "SETUP_PENDING") {
      return <span className="badge bg-warning text-dark">Setup Pending</span>;
    }
    if (status === "ACTIVE") {
      return <span className="badge bg-success">Active</span>;
    }
    return <span className="badge bg-secondary">{status}</span>;
  };

  const columns = [
    { name: "Employee Code", selector: (row) => row.employeeCode, sortable: true },
    { name: "Name", selector: (row) => row.name, sortable: true },
    { name: "Email", selector: (row) => row.email, sortable: true },
    { name: "Department", selector: (row) => row.department, sortable: true },
    { name: "Designation", selector: (row) => row.designation, sortable: true },
    { name: "Status", selector: (row) => row.status, cell: (row) => statusBadge(row.status), sortable: true },
    {
      name: "Action",
      right: true,
      selector: (row) => (
        <Button className="btn btn-sm btn-inox-green border-0" title="View" onClick={() => navigate(`/hr/employee/${row.userId}`)}>
          <i className="fa fa-eye"></i>
        </Button>
      ),
    },
  ];

  return (
    <div className="page-content-bg">
      <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="HR"
        activeSubMenu="EmployeeList"
        activeSubMenuLevel1=""
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div className={menuDrawerShow ? "row main-content-collapsed text-left py-0" : "row main-content text-left py-0"}>
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-sm-6 py-1">
              <Button className="btn btn-sm btn-inox-green border-0 mx-2" onClick={() => setMenuDrawerShow(!menuDrawerShow)}>
                {menuDrawerShow ? <span><i className="fa fa-chevron-left mx-1"></i></span> : <span><i className="fa fa-chevron-right mx-1"></i></span>}
              </Button>
              <span className="page-header-text">Employees</span>
            </div>
          </div>
        </div>

        <div className="col-12 text-left" style={{ marginTop: -5, paddingLeft: 20, paddingRight: 30 }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Employees</h5>
            <Form.Control
              type="text"
              placeholder="Search by name, email, code, department..."
              style={{ maxWidth: 320 }}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <DataTable
            columns={columns}
            data={filteredRows}
            progressPending={dataPending}
            progressComponent={<Spinner className="mx-5 text-inox-green table-spinner" as="span" animation="border" size="lg" role="status" aria-hidden="true" />}
            noDataComponent={<p className="text-center table-no-data">No Data Found</p>}
            pagination
            highlightOnHover
            fixedHeader
            customStyles={userTableCustomStyles}
            dense
            onRowClicked={(row) => navigate(`/hr/employee/${row.userId}`)}
            pointerOnHover
          />
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}

export default EmployeeList;
